// @ts-check

const DEFAULT_RULES = Object.freeze({
  max_lines_per_game: 10,
  minimum_action_gap: 3,
  critical_priority: 90,
  frequency_chances: { off: 0, low: 0.35, normal: 0.7, high: 1 },
});

export class CompanionDirector {
  constructor(pack, options = {}) {
    this.pack = pack;
    this.random = options.random ?? Math.random;
    this.now = options.now ?? (() => new Date());
    this.restore(options.state ?? null);
  }

  restore(state) {
    this.spokenIds = new Set(state?.spoken_ids ?? []);
    this.lastEventAction = new Map(state?.last_event_actions ?? []);
    this.lastSpokenAction = Number.isFinite(state?.last_spoken_action)
      ? state.last_spoken_action
      : -Infinity;
    this.lineCount = state?.line_count ?? 0;
    this.champCount = state?.champ_count ?? 0;
    this.hydrationCount = state?.hydration_count ?? 0;
  }

  reset() {
    this.restore(null);
  }

  snapshot() {
    return {
      spoken_ids: [...this.spokenIds],
      last_event_actions: [...this.lastEventAction],
      last_spoken_action: Number.isFinite(this.lastSpokenAction)
        ? this.lastSpokenAction
        : null,
      line_count: this.lineCount,
      champ_count: this.champCount,
      hydration_count: this.hydrationCount,
    };
  }

  sessionEvent() {
    const hour = this.now().getHours();
    if (hour < 6 || hour >= 23) return "session_late_night";
    if (hour < 12) return "session_morning";
    if (hour < 18) return "session_afternoon";
    return "session_evening";
  }

  line(event, context = {}, options = {}) {
    const rules = { ...DEFAULT_RULES, ...this.pack?.director };
    const frequency = context.frequency ?? "normal";
    if (frequency === "off" && !options.force) return null;
    const action = Number(context.action_count ?? 0);
    const priority = Number(options.priority ?? 0);
    const critical = options.force || priority >= rules.critical_priority;
    if (
      !critical &&
      (this.lineCount >= rules.max_lines_per_game ||
        action - this.lastSpokenAction < rules.minimum_action_gap)
    )
      return null;
    if (
      !critical &&
      this.random() > Number(rules.frequency_chances?.[frequency] ?? 0.7)
    )
      return null;
    const source = this.pack?.companions?.the_jock?.lines?.length
      ? this.pack.companions.the_jock.lines
      : (this.pack?.fallback ?? []);
    let candidates = source.filter(
      (item) =>
        item.event === event &&
        (!item.once_per_game || !this.spokenIds.has(item.id)) &&
        (!this.spokenIds.has(item.id) ||
          source.filter((line) => line.event === event).length === 1) &&
        action - (this.lastEventAction.get(event) ?? -Infinity) >=
          Number(item.cooldown_actions ?? 0),
    );
    candidates = candidates.filter((item) => {
      const normalized = item.text.toLowerCase();
      if (normalized.includes("champ") && this.champCount >= 2) return false;
      if (
        (normalized.includes("water") || normalized.includes("hydrat")) &&
        this.hydrationCount >= 1
      )
        return false;
      return true;
    });
    if (!candidates.length) return null;
    const selected = weightedChoice(candidates, this.random);
    this.spokenIds.add(selected.id);
    this.lastEventAction.set(event, action);
    this.lastSpokenAction = action;
    this.lineCount += 1;
    if (selected.text.toLowerCase().includes("champ")) this.champCount += 1;
    if (/water|hydrat/i.test(selected.text)) this.hydrationCount += 1;
    return {
      id: selected.id,
      text: selected.text,
      voice_id: selected.id,
      event,
    };
  }
}

function weightedChoice(items, random) {
  const total = items.reduce((sum, item) => sum + Number(item.weight ?? 1), 0);
  let cursor = random() * total;
  for (const item of items) {
    cursor -= Number(item.weight ?? 1);
    if (cursor <= 0) return item;
  }
  return items.at(-1);
}
